import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '../../../store';
import { selectUser } from '../../authentication/redux/selectors';

/*
 * The function below is called a selector and allows us to select a value from
 * the state. Selectors can also be defined inline where they're used instead of
 * in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
 */
// selectWorkflowClinicalObservation, selectWorkflowFrequencyData,
// selectWorkflowMolecularImpact, selectWorkflowInterpretation and
// selectWorkflowSummary were removed: that server data is now read from
// RTK Query hooks (useGetVariantSamplesQuery, useGetVariantFrequenciesQuery,
// useGetVariantMolecularImpactHeaderQuery, etc.).
//
// selectWorkflowStep was also removed (unused in production code).

export const selectVariantLock = (state: RootState) =>
  state.variant_workflow.lock;

const selectVariantInstitutes = (state: RootState) =>
  state.variant_workflow.institutes;

export const selectIsVariantReadOnly = createSelector(
  [selectVariantLock, selectUser],
  (lock, user) => {
    if (!lock) {
      return false;
    }
    return lock.lock_type === 'refresh' || lock.locked_by !== user?.username;
  },
);

/**
 * Returns true if the logged-in user's institution is permitted to edit the
 * current variant, based on the `institutes` list returned by the backend.
 * The backend always includes `institutes` in the variant detail response.
 * The Redux slice resets it to `null` while the fetch is in-flight, then sets
 * it to the array from the API payload once the request is fulfilled.
 *  States and their meaning:
 * - `null`   – The variant detail has not loaded yet (fetch still pending):DENY.
 *              Prevents the lock from being acquired before we know
 *              whether the user is allowed.
 * - `[]`     –This is the case for all variants that existed before the
 *              institute feature was introduced: ALLOW.
 *              Treat as unrestricted so old data stays accessible.
 * - `[1, 2]` – The variant is restricted to specific institutes.
 *              : ALLOW only if `user.institution` is in the list.
 *              : DENY otherwise (read-only view, no lock acquired).
 */
export const selectIsUserInstituteAllowed = createSelector(
  [selectVariantInstitutes, selectUser],
  (institutes, user) => {
    if (institutes === null) {
      return false;
    } // still loading — deny until detail is fetched
    if (institutes.length === 0) {
      return true;
    } // no restriction — mirrors backend: institutes.exists() === false
    if (!user?.institution) {
      return false;
    }
    return institutes.includes(Number(user.institution.id));
  },
);

export const selectFrequencyDataNeedsRecalculation = (state: RootState) =>
  state.variant_workflow.frequencyDataNeedsRecalculation;

export const selectIsRecalculatingArguments = (state: RootState) =>
  state.variant_workflow.isRecalculatingArguments;
